import * as XLSX from "xlsx";
import {
  formatPrice,
  getReportStats,
  calculateTotalPrice,
} from "./reportUtils";
import {
  InventoryReport,
  InventoryRoom,
  InventoryItem,
} from "../types/inventory.types";

const getRoomSection = (room: InventoryRoom | undefined): string => {
  if (!room) return "";
  return room.room_section || room.section || "";
};

// Получаем название комнаты из первого товара в комнате
const getRoomNameFromItems = (room: InventoryRoom): string => {
  const correctItems = room["correct-item"] || [];
  const missingItems = room["missing-item"] || [];
  const wrongItems = room["wrong-room-item"] || [];
  
  const allItems = [...correctItems, ...missingItems, ...wrongItems];
  
  if (allItems.length > 0 && allItems[0].room_name) {
    return allItems[0].room_name;
  }
  
  return "Помещение";
};

const getRoomDisplayName = (room: InventoryRoom): string => {
  const roomName = getRoomNameFromItems(room);
  return `${roomName} (№${room.room_number})`;
};

export const generateExcel = (report: InventoryReport) => {
  const worksheetData: any[] = [];
  const stats = getReportStats(report);

  // Заголовок отчета
  worksheetData.push(["ОТЧЕТ ИНВЕНТАРИЗАЦИИ"]);
  worksheetData.push(["Дата:", new Date(report.date).toLocaleString("ru-RU")]);
  worksheetData.push([]);

  // Сводка
  worksheetData.push(["СВОДКА"]);
  worksheetData.push(["Статус", "Количество", "Сумма"]);
  worksheetData.push([
    "Найдено",
    stats.totalCorrect,
    formatPrice(stats.totalCorrectPrice),
  ]);
  worksheetData.push([
    "Отсутствует",
    stats.totalMissing,
    formatPrice(stats.totalMissingPrice),
  ]);
  worksheetData.push([
    "Не на месте",
    stats.totalWrong,
    formatPrice(stats.totalWrongPrice),
  ]);
  worksheetData.push([
    "ВСЕГО",
    stats.totalCorrect + stats.totalMissing + stats.totalWrong,
    formatPrice(
      stats.totalCorrectPrice + stats.totalMissingPrice + stats.totalWrongPrice,
    ),
  ]);
  worksheetData.push([]);
  worksheetData.push([]);

  // Детали по комнатам
  report.rooms?.forEach((room: InventoryRoom) => {
    const roomDisplayName = getRoomDisplayName(room);
    const roomSection = getRoomSection(room);
    const roomNumber = room.room_number;

    // Заголовок комнаты
    worksheetData.push([`ПОМЕЩЕНИЕ: ${roomDisplayName}`]);
    if (roomSection) {
      worksheetData.push([`Секция: ${roomSection}`]);
    }
    worksheetData.push([]);

    const correctItems = room["correct-item"] || [];
    const missingItems = room["missing-item"] || [];
    const wrongItems = room["wrong-room-item"] || [];

    // НАЙДЕННЫЕ МЦ
    if (correctItems.length > 0) {
      worksheetData.push(["НАЙДЕННЫЕ МЦ"]);
      worksheetData.push([
        "Наименование",
        "Помещение",
        "Номер комнаты",
        "Инв. номер",
        "Категория",
        "Стоимость",
      ]);

      correctItems.forEach((item: InventoryItem) => {
        worksheetData.push([
          item.name,
          item.room_name || "—",
          roomNumber,
          item.inv_number || "—",
          item.inventory_tools_type || "Без категории",
          item.price || 0,
        ]);
      });

      const totalCorrectPrice = calculateTotalPrice(correctItems);
      worksheetData.push(["ИТОГО:", "", "", "", "", totalCorrectPrice]);
      worksheetData.push([]);
    }

    // ОТСУТСТВУЮЩИЕ МЦ
    if (missingItems.length > 0) {
      worksheetData.push(["ОТСУТСТВУЮЩИЕ МЦ"]);
      worksheetData.push([
        "Наименование",
        "Помещение",
        "Номер помещения",
        "Инв. номер",
        "Категория",
        "Стоимость",
      ]);

      missingItems.forEach((item: InventoryItem) => {
        worksheetData.push([
          item.name,
          item.room_name || "—",
          roomNumber,
          item.inv_number || "—",
          item.inventory_tools_type || "Без категории",
          item.price || 0,
        ]);
      });

      const totalMissingPrice = calculateTotalPrice(missingItems);
      worksheetData.push(["ИТОГО:", "", "", "", "", totalMissingPrice]);
      worksheetData.push([]);
    }

    // МЦ НЕ НА МЕСТЕ
    if (wrongItems.length > 0) {
      worksheetData.push(["МЦ НЕ НА МЕСТЕ"]);
      worksheetData.push([
        "Наименование",
        "Текущее помещение",
        "Номер помещения",
        "Инв. номер",
        "Категория",
        "Стоимость",
      ]);

      wrongItems.forEach((item: InventoryItem) => {
        worksheetData.push([
          item.name,
          item.room_name || "—",
          roomNumber,
          item.inv_number || "—",
          item.inventory_tools_type || "Без категории",
          item.price || 0,
        ]);
      });

      const totalWrongPrice = calculateTotalPrice(wrongItems);
      worksheetData.push(["ИТОГО:", "", "", "", "", totalWrongPrice]);
      worksheetData.push([]);
    }

    worksheetData.push([]);
  });

  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Настройка ширины колонок
  ws["!cols"] = [
    { wch: 35 }, // Наименование
    { wch: 25 }, // Помещение
    { wch: 15 }, // Номер помещения
    { wch: 15 }, // Инв. номер
    { wch: 20 }, // Категория
    { wch: 15 }, // Стоимость
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Инвентаризация");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};